// client/src/api/services/hotspotService.ts
import { apiClient } from '@/api/client';
import { ENDPOINTS, buildQueryString } from '@/api/endpoints';
import { getErrorMessage } from '@/utils/errorUtils';

// ==================== EXISTING TYPES (Your Original) ====================
export interface Hotspot {
  _id: string;
  user: string;
  name: string;
  ssid: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
    city: string;
    state: string;
    country: string;
  };
  bandwidthLimit: number;
  maxConnections: number;
  hardware: {
    model: string;
    macAddress: string;
  };
  status: string;
  totalBandwidthUsed: number;
  totalEarnings: number;
  createdAt: string;
  updatedAt: string;
  // KX-34 (Kit↔Hotspot↔NasDevice link). The kit linkage and collection country
  // live on the Hotspot model. Operational figures (last heartbeat, sessions,
  // uptime) are NOT on this payload — they come from the authoritative node-cards
  // endpoint (operatorNodeCardService.getNodeCards), joined by _id/hotspotId.
  /** The kit this hotspot/node is linked to (KX-34: Hotspot.kitId). */
  kitId?: string;
  /** Collection country for this node (KX-34: Hotspot.country_code, ISO-2). */
  country_code?: string;
}

export interface CreateHotspotData {
  name: string;
  ssid: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
    city: string;
    state: string;
    country: string;
  };
  bandwidthLimit: number;
  maxConnections: number;
  hardware: {
    model: string;
    macAddress: string;
  };
  // Telecom-grade additive fields (all optional)
  nodeType?: string;
  installationType?: string;
  vendor?: string;
  serialNumber?: string;
  internetType?: string;
  antennaType?: string;
}

export interface HotspotStatsDataPoint {
  month?: string;
  week?: string;
  day?: string;
  bandwidthUsedGB: string;
  earnings: string;
}

export interface HotspotStatsResponse {
  success: boolean;
  hotspotId: string;
  range: 'daily' | 'weekly' | 'monthly';
  stats: HotspotStatsDataPoint[];
}

export interface HotspotRealtimeStatusResponse {
  success: boolean;
  id: string;
  name: string;
  status: string;
}

export interface AvailableHotspot {
  id?: string;
  name: string;
  ssid: string;
  signalStrength?: number;
  status?: string;
  _id?: string;
  location?: {
    latitude: number;
    longitude: number;
    address: string;
    city: string;
    state: string;
    country: string;
  };
  hardware?: {
    model: string;
    macAddress: string;
  };
  bandwidthLimit?: number;
  maxConnections?: number;
  totalBandwidthUsed?: number;
  totalEarnings?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface AvailableHotspotsResponse {
  success: boolean;
  message: string;
  hotspots: AvailableHotspot[];
}

export interface HotspotResponse {
  success: boolean;
  message: string;
  hotspot: Hotspot;
}

export interface HotspotsListResponse {
  success: boolean;
  hotspots: Hotspot[];
  total?: number;
}

// ==================== NEW TYPES (For End User Discovery) ====================
export interface EndUserHotspot {
  id: string;
  name: string;
  provider: string;
  location: {
    address: string;
    city: string;
    coordinates: {
      lat: number;
      lng: number;
    };
    /** Legacy field – prefer `coordinates.lat` */
    latitude?: number;
    /** Legacy field – prefer `coordinates.lng` */
    longitude?: number;
  };
  speed: number;
  speedUnit: string;
  signalStrength: number;
  userCount: number;
  maxConnections?: number;
  distance: number;
  distanceUnit: string;
  status: 'active' | 'inactive' | 'maintenance';
  isOnline?: boolean;
  isNew?: boolean;
  lastSeen?: string;
  amenities?: string[];
  operatingHours?: {
    open: string;
    close: string;
  };
}

export interface HotspotSearchParams {
  search?: string;
  city?: string;
  speed?: string;
  status?: string;
  page?: number;
  limit?: number;
  latitude?: number;
  longitude?: number;
}

export interface EndUserHotspotResponse<T> {
  success: boolean;
  data: T;
}

export interface HotspotListResponse {
  hotspots: EndUserHotspot[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ConnectionResponse {
  connectionId: string;
  sessionToken: string;
  hotspotId: string;
  connectedAt: string;
  expiresAt: string;
}

export interface DisconnectionResponse {
  connectionId: string;
  disconnectedAt: string;
  duration: number;
  dataUsed: number;
}

// ==================== EXISTING FUNCTIONS (Your Original) ====================

/**
 * Fetch all hotspots for the authenticated user (Operator)
 */
export const getHotspots = async (): Promise<Hotspot[]> => {
  try {
    const response = await apiClient.get<HotspotsListResponse>(ENDPOINTS.HOTSPOT.LIST);
    return response.data.hotspots || [];
  } catch (error: unknown) {
    console.error('Error fetching hotspots:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch hotspots');
  }
};

/**
 * Get search hotspots (End User - Your existing function)
 */
export const getSearchHotspots = async (): Promise<unknown> => {
  try {
    const response = await apiClient.get<Record<string, unknown>>(ENDPOINTS.HOTSPOT.GET);
    return response.data || [];
  } catch (error: unknown) {
    console.error('Error fetching hotspots:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch hotspots');
  }
};

/**
 * Connect to hotspot (Your existing function)
 */
export const connectHotspots = async (id: string): Promise<unknown> => {
  try {
    const response = await apiClient.post<Record<string, unknown>>(ENDPOINTS.HOTSPOT.CONNECT(id), { deviceId: id });
    return response.data || [];
  } catch (error: unknown) {
    console.error('Error connecting to hotspot:', error);
    throw new Error(getErrorMessage(error) || 'Failed to connect to hotspot');
  }
};

/**
 * Create a new hotspot
 */
export const createHotspot = async (data: CreateHotspotData): Promise<Hotspot> => {
  try {
    const response = await apiClient.post<HotspotResponse>(ENDPOINTS.HOTSPOT.LIST, data);
    return response.data.hotspot;
  } catch (error: unknown) {
    console.error('Error creating hotspot:', error);
    throw new Error(getErrorMessage(error) || 'Failed to create hotspot');
  }
};

/**
 * Update an existing hotspot
 */
export const updateHotspot = async (id: string, data: Partial<CreateHotspotData>): Promise<Hotspot> => {
  try {
    const response = await apiClient.put<HotspotResponse>(`${ENDPOINTS.HOTSPOT.LIST}/${id}`, data);
    return response.data.hotspot;
  } catch (error: unknown) {
    console.error('Error updating hotspot:', error);
    throw new Error(getErrorMessage(error) || 'Failed to update hotspot');
  }
};

/**
 * Delete a hotspot
 */
export const deleteHotspot = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`${ENDPOINTS.HOTSPOT.LIST}/${id}`);
  } catch (error: unknown) {
    console.error('Error deleting hotspot:', error);
    throw new Error(getErrorMessage(error) || 'Failed to delete hotspot');
  }
};

/**
 * Get a single hotspot by ID
 */
export const getHotspotById = async (id: string): Promise<Hotspot> => {
  try {
    const response = await apiClient.get<HotspotResponse>(`${ENDPOINTS.HOTSPOT.LIST}/${id}`);
    return response.data.hotspot;
  } catch (error: unknown) {
    console.error('Error fetching hotspot:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch hotspot');
  }
};

/**
 * Get hotspot stats (daily, weekly, or monthly)
 */
export const getHotspotStats = async (
  id: string,
  range: 'daily' | 'weekly' | 'monthly' = 'daily'
): Promise<HotspotStatsResponse> => {
  try {
    const response = await apiClient.get<HotspotStatsResponse>(
      `${ENDPOINTS.HOTSPOT.LIST}/${id}/stats?range=${range}`
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching hotspot stats:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch hotspot stats');
  }
};

/**
 * Get real-time status of a hotspot
 */
export const getHotspotRealtimeStatus = async (id: string): Promise<HotspotRealtimeStatusResponse> => {
  try {
    const response = await apiClient.get<HotspotRealtimeStatusResponse>(
      `${ENDPOINTS.HOTSPOT.LIST}/${id}/status`
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching hotspot status:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch hotspot status');
  }
};

/**
 * Get available hotspots for setup
 */
export const getAvailableHotspots = async (): Promise<AvailableHotspot[]> => {
  try {
    const response = await apiClient.get<AvailableHotspotsResponse>(
      `${ENDPOINTS.HOTSPOT.LIST}/setup/available`
    );
    return response.data.hotspots || [];
  } catch (error: unknown) {
    console.error('Error fetching available hotspots:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch available hotspots');
  }
};

/**
 * Get aggregated stats for all hotspots
 */
export const getAllHotspotsStats = async (
  range: 'daily' | 'weekly' | 'monthly' = 'daily'
): Promise<{ [hotspotId: string]: HotspotStatsResponse }> => {
  try {
    const hotspots = await getHotspots();
    const statsPromises = hotspots.map(async (hotspot) => {
      try {
        const stats = await getHotspotStats(hotspot._id, range);
        return { id: hotspot._id, stats };
      } catch (error: unknown) {
        console.error(`Failed to fetch stats for ${hotspot.name}`);
        return { id: hotspot._id, stats: null };
      }
    });

    const results = await Promise.all(statsPromises);

    const statsMap: { [hotspotId: string]: HotspotStatsResponse } = {};
    results.forEach(({ id, stats }) => {
      if (stats) {
        statsMap[id] = stats;
      }
    });

    return statsMap;
  } catch (error: unknown) {
    console.error('Error fetching all hotspots stats:', error);
    throw new Error('Failed to fetch stats for all hotspots');
  }
};

/**
 * Aggregate stats from multiple hotspots
 */
export const aggregateStats = (
  statsMap: { [hotspotId: string]: HotspotStatsResponse }
): HotspotStatsDataPoint[] => {
  const dateMap: { [period: string]: { bandwidth: number; earnings: number } } = {};

  Object.values(statsMap).forEach((statsResponse) => {
    if (!statsResponse || !statsResponse.stats) return;

    statsResponse.stats.forEach((item) => {
      const period = item.month || item.week || item.day || 'unknown';

      if (!dateMap[period]) {
        dateMap[period] = { bandwidth: 0, earnings: 0 };
      }

      dateMap[period].bandwidth += parseFloat(item.bandwidthUsedGB || '0');
      dateMap[period].earnings += parseFloat(item.earnings || '0');
    });
  });

  const aggregated: HotspotStatsDataPoint[] = Object.entries(dateMap).map(([period, data]) => ({
    month: period,
    bandwidthUsedGB: data.bandwidth.toFixed(2),
    earnings: data.earnings.toFixed(2),
  }));

  return aggregated;
};

// ==================== NEW FUNCTIONS (For End User Discovery) ====================

/**
 * Search and list hotspots with filters (End User)
 */
export const searchHotspots = async (
  params: HotspotSearchParams = {}
): Promise<EndUserHotspotResponse<HotspotListResponse>> => {
  try {
    // Get user's location if available (for distance calculation)
    let userLocation = { latitude: params.latitude, longitude: params.longitude };
    
    // If no location provided, try to get from browser
    if (!userLocation.latitude || !userLocation.longitude) {
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });
          userLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
        } catch (geoError) {
          console.warn('Geolocation not available:', geoError);
        }
      }
    }

    const queryString = buildQueryString({
      search: params.search,
      city: params.city && params.city !== 'All Cities' ? params.city : undefined,
      speed: params.speed && params.speed !== 'Any Bandwidth' ? params.speed : undefined,
      status: params.status && params.status !== 'All Status' ? params.status : undefined,
      page: params.page || 1,
      limit: params.limit || 10,
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
    });

    const response = await apiClient.get<Record<string, unknown>>(
      `${ENDPOINTS.HOTSPOT.GET}${queryString}`
    );

    console.log('Raw API Response:', response.data);

    // Handle the response structure from your backend
    if ((response.data as unknown as Record<string, unknown>)?.success && ((response.data as unknown as Record<string, unknown>)?.data as unknown as Record<string, unknown> | undefined)?.hotspots) {
      return {
        success: true,
        data: {
          hotspots: ((response.data as unknown as Record<string, unknown>).data as unknown as Record<string, unknown>).hotspots as EndUserHotspot[],
          pagination: ((response.data as unknown as Record<string, unknown>).data as unknown as Record<string, unknown>).pagination as { total: number; page: number; limit: number; totalPages: number; }
        }
      };
    } else if (((response.data as unknown as Record<string, unknown>)?.data as unknown as Record<string, unknown> | undefined)?.hotspots) {
      return {
        success: true,
        data: {
          hotspots: ((response.data as unknown as Record<string, unknown>).data as unknown as Record<string, unknown>).hotspots as EndUserHotspot[],
          pagination: (((response.data as unknown as Record<string, unknown>).data as unknown as Record<string, unknown>).pagination as { total: number; page: number; limit: number; totalPages: number; }) || {
            total: (((response.data as unknown as Record<string, unknown>).data as unknown as Record<string, unknown>).hotspots as EndUserHotspot[]).length,
            page: params.page || 1,
            limit: params.limit || 10,
            totalPages: Math.ceil((((response.data as unknown as Record<string, unknown>).data as unknown as Record<string, unknown>).hotspots as EndUserHotspot[]).length / (params.limit || 10))
          }
        }
      };
    } else {
      console.error('Unexpected response structure:', response.data);
      throw new Error('Unexpected response structure from API');
    }
  } catch (error: unknown) {
    console.error('Error searching hotspots:', error);
    console.error('Error response:', (error as { response?: { data?: unknown } })?.response?.data);
    throw new Error(getErrorMessage(error) || 'Failed to search hotspots');
  }
};

/**
 * Get hotspot details by ID (End User)
 */
export const getHotspotDetails = async (
  hotspotId: string,
  latitude?: number,
  longitude?: number
): Promise<EndUserHotspotResponse<EndUserHotspot>> => {
  try {
    // Build query string with location if provided
    const queryString = buildQueryString({
      latitude,
      longitude
    });

    const response = await apiClient.get<Record<string, unknown>>(
      `${ENDPOINTS.HOTSPOT.GET}${hotspotId}${queryString}`
    );

    console.log('Hotspot details response:', response.data);

    // Handle your backend response structure
    if ((response.data as unknown as Record<string, unknown>)?.success && (response.data as unknown as Record<string, unknown>)?.data) {
      return {
        success: true,
        data: (response.data as unknown as Record<string, unknown>).data as EndUserHotspot
      };
    } else if ((response.data as unknown as Record<string, unknown>)?.data) {
      return {
        success: true,
        data: (response.data as unknown as Record<string, unknown>).data as EndUserHotspot
      };
    } else {
      throw new Error('Unexpected response structure');
    }
  } catch (error: unknown) {
    console.error('Error fetching hotspot details:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch hotspot details');
  }
};

/**
 * Connect to a hotspot (End User - Enhanced version)
 */
export const connectToHotspot = async (
  hotspotId: string,
  deviceId?: string
): Promise<EndUserHotspotResponse<ConnectionResponse>> => {
  try {
    const payload: unknown = {};
    if (deviceId) {
      (payload as unknown as Record<string, unknown>).deviceId = deviceId;
    }

    const response = await apiClient.post<EndUserHotspotResponse<ConnectionResponse>>(
      ENDPOINTS.HOTSPOT.CONNECT(hotspotId),
      Object.keys(payload).length > 0 ? payload : undefined
    );

    return response.data;
  } catch (error: unknown) {
    console.error('Error connecting to hotspot:', error);

    // Handle specific error cases
    const err = error as { response?: { status?: number; data?: { error?: { code?: string; message?: string } } } };
    if (err.response?.status === 400) {
      const errorCode = err.response?.data?.error?.code;

      switch (errorCode) {
        case 'NO_ACTIVE_SUBSCRIPTION':
          throw new Error('You need an active subscription to connect to hotspots');
        case 'HOTSPOT_UNAVAILABLE':
          throw new Error('This hotspot is currently unavailable');
        case 'CONNECTION_LIMIT_REACHED':
          throw new Error('Maximum device connections reached');
        case 'DEVICE_ALREADY_CONNECTED':
          throw new Error('This device is already connected to a hotspot');
        case 'DATA_LIMIT_EXCEEDED':
          throw new Error('You have exceeded your data limit');
        case 'HOTSPOT_OUT_OF_RANGE':
          throw new Error('You are too far from this hotspot');
        default:
          console.log('third case', err.response?.data?.error?.message);
          throw new Error(JSON.stringify(err.response?.data?.error) || 'Failed to connect to hotspot');
      }
    }

    if (err.response?.status === 404) {
      throw new Error('Hotspot not found');
    }

    throw new Error(getErrorMessage(error) || 'Failed to connect to hotspot');
  }
};

/**
 * Disconnect from a hotspot (End User)
 */
export const disconnectFromHotspot = async (
  hotspotId: string,
  connectionId: string
): Promise<EndUserHotspotResponse<DisconnectionResponse>> => {
  try {
    const response = await apiClient.post<EndUserHotspotResponse<DisconnectionResponse>>(
      ENDPOINTS.HOTSPOT.DISCONNECT(connectionId),
      { connectionId: connectionId, deviceId: connectionId }
    );

    return response.data;
  } catch (error: unknown) {
    console.error('Error disconnecting from hotspot:', error);
    throw new Error(getErrorMessage(error) || 'Failed to disconnect from hotspot');
  }
};

/**
 * Get nearby hotspots based on location (End User)
 */
export const getNearbyHotspots = async (
  latitude: number,
  longitude: number,
  limit: number = 5
): Promise<EndUserHotspotResponse<{ nearbyHotspots: EndUserHotspot[]; userLocation: { latitude: number; longitude: number } }>> => {
  try {
    const queryString = buildQueryString({ latitude, longitude, limit });

    const response = await apiClient.get<EndUserHotspotResponse<Record<string, unknown>>>(
      `${ENDPOINTS.DASHBOARD.NEARBY_HOTSPOTS}${queryString}`
    );

    return response.data as EndUserHotspotResponse<{ nearbyHotspots: EndUserHotspot[]; userLocation: { latitude: number; longitude: number } }>;
  } catch (error: unknown) {
    console.error('Error fetching nearby hotspots:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch nearby hotspots');
  }
};

/**
 * Quick connect to nearest hotspot (End User)
 */
export const quickConnect = async (
  latitude: number,
  longitude: number,
  deviceId?: string
): Promise<EndUserHotspotResponse<ConnectionResponse & { hotspot: { id: string; name: string; provider: string } }>> => {
  try {
    const payload: unknown = { latitude, longitude };
    if (deviceId) {
      (payload as unknown as Record<string, unknown>).deviceId = deviceId;
    }

    const response = await apiClient.post<EndUserHotspotResponse<Record<string, unknown>>>(
      ENDPOINTS.DASHBOARD.QUICK_CONNECT,
      payload
    );

    return response.data as unknown as EndUserHotspotResponse<ConnectionResponse & { hotspot: { id: string; name: string; provider: string } }>;
  } catch (error: unknown) {
    console.error('Error quick connecting:', error);

    const err = error as { response?: { status?: number; data?: { error?: { code?: string } } } };
    if (err.response?.status === 404) {
      throw new Error('No available hotspots found nearby');
    }

    if (err.response?.status === 400) {
      const errorCode = err.response?.data?.error?.code;
      if (errorCode === 'NO_ACTIVE_SUBSCRIPTION') {
        throw new Error('You need an active subscription to connect to hotspots');
      }
    }

    throw new Error(getErrorMessage(error) || 'Failed to connect to hotspot');
  }
};

/**
 * Get hotspots for map view (End User)
 */
export const getHotspotsForMap = async (
  bounds: string
): Promise<EndUserHotspotResponse<{ hotspots: Array<{ id: string; name: string; coordinates: { lat: number; lng: number }; signalStrength: number; status: string }> }>> => {
  try {
    const queryString = buildQueryString({ bounds });

    const response = await apiClient.get<EndUserHotspotResponse<Record<string, unknown>>>(
      `${ENDPOINTS.HOTSPOT.MAP_VIEW}${queryString}`
    );

    return response.data as EndUserHotspotResponse<{ hotspots: Array<{ id: string; name: string; coordinates: { lat: number; lng: number }; signalStrength: number; status: string }> }>;
  } catch (error: unknown) {
    console.error('Error fetching hotspots for map:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch hotspots for map');
  }
};

/**
 * Advanced filter hotspots (End User)
 */
export const filterHotspots = async (
  filters: {
    location?: {
      latitude: number;
      longitude: number;
      radius: number;
      radiusUnit?: string;
    };
    filters?: {
      minSpeed?: number;
      minSignalStrength?: number;
      providers?: string[];
      amenities?: string[];
      availability?: string;
    };
    sort?: {
      by: string;
      order: 'asc' | 'desc';
    };
    page?: number;
    limit?: number;
  }
): Promise<EndUserHotspotResponse<HotspotListResponse>> => {
  try {
    const response = await apiClient.post<EndUserHotspotResponse<HotspotListResponse>>(
      ENDPOINTS.HOTSPOT.FILTER,
      filters
    );

    return response.data;
  } catch (error: unknown) {
    console.error('Error filtering hotspots:', error);
    throw new Error(getErrorMessage(error) || 'Failed to filter hotspots');
  }
};


/**
 * Fetch currently-connected users for a hotspot (End User)
 */
export interface ConnectedUser {
  connectionId: string;
  name: string;
  connectedAt: string | null;
  durationMinutes: number | null;
}

export interface ConnectedUsersResponse {
  hotspotId: string;
  hotspotName: string;
  userCount: number;
  users: ConnectedUser[];
}

export const getConnectedUsers = async (
  hotspotId: string
): Promise<EndUserHotspotResponse<ConnectedUsersResponse>> => {
  try {
    const response = await apiClient.get<Record<string, unknown>>(
      `${ENDPOINTS.HOTSPOT.GET}${hotspotId}/connected-users`
    );
    if ((response.data as unknown as Record<string, unknown>)?.success && (response.data as unknown as Record<string, unknown>)?.data) {
      return { success: true, data: (response.data as unknown as Record<string, unknown>).data as ConnectedUsersResponse };
    }
    throw new Error('Unexpected response structure');
  } catch (error: unknown) {
    console.error('Error fetching connected users:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch connected users');
  }
};


/**
 * Fetch the user's currently-active connection (if any). The source of
 * truth for which hotspot the user is connected to.
 */
export interface MyActiveConnection {
  connectionId: string;
  hotspotId: string;
  hotspotName: string | null;
  connectedAt: string | null;
}

export const getMyActiveConnection = async (): Promise<MyActiveConnection | null> => {
  try {
    const response = await apiClient.get<Record<string, unknown>>(
      `${ENDPOINTS.HOTSPOT.GET}my-active-connection`
    );
    if ((response.data as unknown as Record<string, unknown>)?.success) {
      return ((response.data as unknown as Record<string, unknown>).data as Record<string, unknown>)?.connection as MyActiveConnection | null;
    }
    return null;
  } catch (error: unknown) {
    console.error('Error fetching active connection:', error);
    return null;
  }
};

// ==================== EXPORTS ====================
export default {
  // Operator functions (existing)
  getHotspots,
  createHotspot,
  updateHotspot,
  deleteHotspot,
  getHotspotById,
  getHotspotStats,
  getHotspotRealtimeStatus,
  getAvailableHotspots,
  getAllHotspotsStats,
  aggregateStats,
  getSearchHotspots,
  connectHotspots,
  
  // End User functions (new)
  searchHotspots,
  getHotspotDetails,
  connectToHotspot,
  disconnectFromHotspot,
  getNearbyHotspots,
  quickConnect,
  getHotspotsForMap,
  getConnectedUsers,
  getMyActiveConnection,
  filterHotspots,
};
/**
 * Record a proof-of-location witness reading for a hotspot (End User, web).
 * Fire-and-forget: asks the browser for location and posts it in the
 * background. Never throws — if permission is denied or it times out, it
 * silently does nothing so the connect flow is never affected.
 */
export const captureAndRecordWitness = (hotspotId: string): void => {
  try {
    if (!hotspotId || typeof navigator === 'undefined' || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const body = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          source: 'web',
        };
        apiClient
          .post(ENDPOINTS.HOTSPOT.WITNESS(hotspotId), body)
          .catch(() => { /* silent: witness is best-effort */ });
      },
      () => { /* permission denied / unavailable: do nothing */ },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  } catch {
    /* never let witness capture affect the user */
  }
};
